import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';

const ACCOUNT_TYPES = [
  { value: 'ASSET', label: 'Asset' },
  { value: 'LIABILITY', label: 'Liability' },
  { value: 'EQUITY', label: 'Equity' },
  { value: 'INCOME', label: 'Income' },
  { value: 'EXPENSE', label: 'Expense' },
] as const;

const ACCOUNT_SUB_TYPES: Record<string, { value: string; label: string }[]> = {
  ASSET: [
    { value: 'CASH', label: 'Cash' },
    { value: 'BANK', label: 'Bank' },
    { value: 'ACCOUNTS_RECEIVABLE', label: 'Accounts Receivable' },
    { value: 'INVENTORY', label: 'Inventory' },
    { value: 'PREPAID_EXPENSE', label: 'Prepaid Expense' },
    { value: 'FIXED_ASSET', label: 'Fixed Asset' },
    { value: 'ACCUMULATED_DEPRECIATION', label: 'Accumulated Depreciation' },
    { value: 'OTHER_ASSET', label: 'Other Asset' },
  ],
  LIABILITY: [
    { value: 'ACCOUNTS_PAYABLE', label: 'Accounts Payable' },
    { value: 'CREDIT_CARD', label: 'Credit Card' },
    { value: 'ACCRUED_EXPENSE', label: 'Accrued Expense' },
    { value: 'UNEARNED_REVENUE', label: 'Unearned Revenue' },
    { value: 'LOANS_PAYABLE', label: 'Loans Payable' },
    { value: 'OTHER_LIABILITY', label: 'Other Liability' },
  ],
  EQUITY: [
    { value: 'OWNERS_EQUITY', label: "Owner's Equity" },
    { value: 'RETAINED_EARNINGS', label: 'Retained Earnings' },
    { value: 'COMMON_STOCK', label: 'Common Stock' },
  ],
  INCOME: [
    { value: 'SALES_REVENUE', label: 'Sales Revenue' },
    { value: 'SERVICE_REVENUE', label: 'Service Revenue' },
    { value: 'COMMISSION_INCOME', label: 'Commission Income' },
    { value: 'OTHER_INCOME', label: 'Other Income' },
  ],
  EXPENSE: [
    { value: 'COST_OF_SALES', label: 'Cost of Sales' },
    { value: 'OPERATING_EXPENSE', label: 'Operating Expense' },
    { value: 'PAYROLL_EXPENSE', label: 'Payroll Expense' },
    { value: 'DEPRECIATION_EXPENSE', label: 'Depreciation Expense' },
    { value: 'INTEREST_EXPENSE', label: 'Interest Expense' },
    { value: 'TAX_EXPENSE', label: 'Tax Expense' },
    { value: 'OTHER_EXPENSE', label: 'Other Expense' },
  ],
};

const NORMAL_BALANCE_BY_TYPE: Record<string, 'DEBIT' | 'CREDIT'> = {
  ASSET: 'DEBIT',
  EXPENSE: 'DEBIT',
  LIABILITY: 'CREDIT',
  EQUITY: 'CREDIT',
  INCOME: 'CREDIT',
};

type AccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
type AccountSubType =
  | 'CASH'
  | 'BANK'
  | 'ACCOUNTS_RECEIVABLE'
  | 'INVENTORY'
  | 'PREPAID_EXPENSE'
  | 'FIXED_ASSET'
  | 'ACCUMULATED_DEPRECIATION'
  | 'OTHER_ASSET'
  | 'ACCOUNTS_PAYABLE'
  | 'CREDIT_CARD'
  | 'ACCRUED_EXPENSE'
  | 'UNEARNED_REVENUE'
  | 'LOANS_PAYABLE'
  | 'OTHER_LIABILITY'
  | 'OWNERS_EQUITY'
  | 'RETAINED_EARNINGS'
  | 'COMMON_STOCK'
  | 'SALES_REVENUE'
  | 'SERVICE_REVENUE'
  | 'COMMISSION_INCOME'
  | 'OTHER_INCOME'
  | 'COST_OF_SALES'
  | 'OPERATING_EXPENSE'
  | 'PAYROLL_EXPENSE'
  | 'DEPRECIATION_EXPENSE'
  | 'INTEREST_EXPENSE'
  | 'TAX_EXPENSE'
  | 'OTHER_EXPENSE';

const accountSchema = z.object({
  code: z.string().min(1, 'Account code is required'),
  name: z.string().min(1, 'Account name is required'),
  type: z.enum(['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE']),
  subType: z.string().optional(),
  parentId: z.string().optional(),
  description: z.string().optional(),
  currency: z.string().default('TRY'),
});

type AccountFormData = z.infer<typeof accountSchema>;

interface Account {
  id: string;
  code: string;
  name: string;
  type: AccountType;
  subType?: AccountSubType | null;
  parentId?: string | null;
  description?: string | null;
  currency: string;
  isSystemAccount: boolean;
}

interface AccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account?: Account;
  /**
   * Creating a child under this account: parent and a suggested next code are
   * pre-filled, so adding the 71st child of a branch does not require knowing
   * the 70 codes already taken.
   */
  parentNode?: { id: string; code: string; type: string };
  onSuccess?: () => void;
}

export function AccountModal({
  open,
  onOpenChange,
  account,
  parentNode,
  onSuccess,
}: AccountModalProps) {
  const { t } = useTranslation('accounting');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const isEditing = !!account;

  // Preparation mode (development) unlocks everything: code, hesap türü, para
  // birimi, and editing system accounts. In production the chart is locked and
  // the form falls back to the read-only fields it has always had.
  const { data: chartPrep } = trpc.account.chartPrepStatus.useQuery(undefined, { enabled: open });
  const { data: codeSuggestion } = trpc.accountWorkbench.suggestCode.useQuery(
    { parentCode: parentNode?.code ?? '' },
    { enabled: open && !account && !!parentNode }
  );
  const unlocked = chartPrep?.enabled === true;
  const systemLocked = !unlocked && account?.isSystemAccount === true;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<AccountFormData>({
    defaultValues: account
      ? {
          code: account.code,
          name: account.name,
          type: account.type,
          subType: account.subType ?? undefined,
          parentId: account.parentId ?? undefined,
          description: account.description ?? undefined,
          currency: account.currency,
        }
      : {
          type: 'ASSET',
          currency: 'TRY',
        },
  });

  const selectedType = watch('type');
  const parentId = watch('parentId');

  // Reset form when account changes
  useEffect(() => {
    setWarnings([]);
    setSaveError(null);
    setIsSubmitting(false);
    if (account) {
      reset({
        code: account.code,
        name: account.name,
        type: account.type,
        subType: account.subType ?? undefined,
        parentId: account.parentId ?? undefined,
        description: account.description ?? undefined,
        currency: account.currency,
      });
    } else if (parentNode) {
      reset({
        code: codeSuggestion?.next ?? '',
        name: '',
        type: parentNode.type as AccountType,
        parentId: parentNode.id,
        currency: 'TRY',
      });
    } else {
      reset({
        type: 'ASSET',
        currency: 'TRY',
      });
    }
  }, [open, account, parentNode, codeSuggestion, reset]);

  // Fetch parent accounts for dropdown.
  // limit must cover the whole type: the default page of 100 (ordered by code)
  // stopped at 120.x, so every 2xx+ account was unpickable as a parent — which
  // is how deep codes ended up created as roots.
  const { data: parentAccounts } = trpc.account.list.useQuery(
    {
      type: selectedType,
      isActive: true,
      limit: 1000,
    },
    { enabled: open }
  );

  const utils = trpc.useUtils();

  const createMutation = trpc.account.create.useMutation({
    onSuccess: () => {
      utils.account.list.invalidate();
      utils.account.getTree.invalidate();
      utils.account.stats.invalidate();
      reset();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      setSaveError(error.message);
      setIsSubmitting(false);
    },
  });

  const updateMutation = trpc.account.update.useMutation({
    onSuccess: (result) => {
      utils.account.list.invalidate();
      utils.account.getTree.invalidate();
      utils.account.byId.invalidate();
      utils.account.stats.invalidate();
      onSuccess?.();
      setIsSubmitting(false);
      // The change is saved either way; when it moved something the reports or
      // the posting catalog key off, hold the dialog open so it gets read.
      const notes = result?.warnings ?? [];
      if (notes.length > 0) {
        setWarnings(notes);
        return;
      }
      onOpenChange(false);
    },
    onError: (error) => {
      setSaveError(error.message);
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: AccountFormData) => {
    setIsSubmitting(true);
    setSaveError(null);

    if (isEditing && account) {
      setWarnings([]);
      updateMutation.mutate({
        id: account.id,
        // Only send code if it actually changed (avoids the uniqueness check on no-op edits)
        ...(data.code !== account.code ? { code: data.code } : {}),
        name: data.name,
        description: data.description || null,
        parentId: data.parentId || null,
        subType: (data.subType as AccountSubType | undefined) ?? null,
        // Preparation-mode fields; the server refuses them on a locked chart,
        // so only send what actually changed.
        ...(unlocked && data.type !== account.type
          ? { type: data.type, normalBalance: NORMAL_BALANCE_BY_TYPE[data.type] }
          : {}),
        ...(unlocked && data.currency !== account.currency ? { currency: data.currency } : {}),
      });
    } else {
      createMutation.mutate({
        code: data.code,
        name: data.name,
        type: data.type,
        subType: data.subType as AccountSubType | undefined,
        parentId: data.parentId || undefined,
        description: data.description,
        normalBalance: NORMAL_BALANCE_BY_TYPE[data.type],
        currency: data.currency,
      });
    }
  };

  const availableSubTypes = ACCOUNT_SUB_TYPES[selectedType] || [];
  const availableParents = parentAccounts?.accounts.filter((a) => a.id !== account?.id) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-lg flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('accountModal.editAccount') : t('accountModal.newAccount')}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t('accountModal.editDescription') : t('accountModal.createDescription')}
          </DialogDescription>
          {saveError && <div role="alert" className="select-text break-words rounded-md bg-red-50 p-3 text-sm text-red-700">{saveError}</div>}
          {unlocked && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {t('accountModal.prepModeNotice')}
            </p>
          )}
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          {/* Only the fields scroll; the header and the buttons stay put. */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            {/* Account Code */}
            <div className="space-y-2">
              <Label htmlFor="code">{t('accountModal.accountCode')} *</Label>
              <Input
                id="code"
                {...register('code')}
                placeholder={t('accountModal.accountCodePlaceholder')}
                disabled={systemLocked}
                className="font-mono"
              />
              {errors.code && <p className="text-sm text-red-500">{errors.code.message}</p>}
              {!isEditing && codeSuggestion && codeSuggestion.gaps.length > 0 && (
                <p className="text-muted-foreground text-xs">
                  {t('accountModal.codeGaps')}{' '}
                  {codeSuggestion.gaps.slice(0, 6).map((gap) => (
                    <button
                      key={gap}
                      type="button"
                      className="text-primary mr-1.5 font-mono underline"
                      onClick={() => setValue('code', gap)}
                    >
                      {gap}
                    </button>
                  ))}
                </p>
              )}
              {isEditing && unlocked && (
                <p className="text-xs text-amber-600">{t('accountModal.codeCascadeWarning')}</p>
              )}
              {isEditing && !unlocked && account?.isSystemAccount !== true && (
                <p className="text-xs text-amber-600">{t('accountModal.codeChangeWarning')}</p>
              )}
              {isEditing && systemLocked && (
                <p className="text-muted-foreground text-xs">
                  {t('accountModal.codeCannotChangeSystem')}
                </p>
              )}
            </div>

            {/* Account Name */}
            <div className="space-y-2">
              <Label htmlFor="name">{t('accountModal.accountName')} *</Label>
              <Input
                id="name"
                {...register('name')}
                placeholder={t('accountModal.accountNamePlaceholder')}
              />
              {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
            </div>

            {/* Account Type */}
            <div className="space-y-2">
              <Label>{t('accountModal.accountType')} *</Label>
              <Combobox
                options={ACCOUNT_TYPES.map((type) => ({
                  value: type.value,
                  label: type.label,
                }))}
                value={selectedType}
                onValueChange={(value) => {
                  setValue('type', value as AccountType);
                  setValue('subType', undefined);
                  setValue('parentId', undefined);
                }}
                disabled={(isEditing && !unlocked) || systemLocked}
                placeholder={t('accountModal.selectType')}
                searchPlaceholder="Search..."
              />
              {isEditing && !unlocked && (
                <p className="text-muted-foreground text-xs">
                  {t('accountModal.accountTypeReadonly')}
                </p>
              )}
              {isEditing && unlocked && (
                <p className="text-xs text-amber-600">{t('accountModal.typeChangeWarning')}</p>
              )}
            </div>

            {/* Sub-Type */}
            {availableSubTypes.length > 0 && (
              <div className="space-y-2">
                <Label>{t('accountModal.subType')}</Label>
                <Combobox
                  options={[
                    { value: '__none__', label: 'None' },
                    ...availableSubTypes.map((subType) => ({
                      value: subType.value,
                      label: subType.label,
                    })),
                  ]}
                  value={watch('subType') || '__none__'}
                  onValueChange={(value) =>
                    setValue('subType', value === '__none__' ? undefined : value)
                  }
                  disabled={systemLocked}
                  placeholder={t('accountModal.selectSubType')}
                  searchPlaceholder="Search..."
                />
              </div>
            )}

            {/* Parent Account */}
            {availableParents.length > 0 && (
              <div className="space-y-2">
                <Label>{t('accountModal.parentAccount')}</Label>
                <Combobox
                  options={[
                    { value: '__none__', label: t('accountModal.noneTopLevel') },
                    ...availableParents.map((parent) => ({
                      value: parent.id,
                      label: `${parent.code} - ${parent.name}`,
                    })),
                  ]}
                  value={parentId || '__none__'}
                  onValueChange={(value) =>
                    setValue('parentId', value === '__none__' ? undefined : value)
                  }
                  disabled={systemLocked}
                  placeholder={t('accountModal.selectParent')}
                  searchPlaceholder={t('accountModal.searchAccounts')}
                />
                <p className="text-muted-foreground text-xs">{t('accountModal.parentSameType')}</p>
              </div>
            )}

            {/* Currency */}
            <div className="space-y-2">
              <Label>{t('accountModal.currency')}</Label>
              <Combobox
                options={[
                  { value: 'TRY', label: 'TRY - Turkish Lira' },
                  { value: 'USD', label: 'USD - US Dollar' },
                  { value: 'EUR', label: 'EUR - Euro' },
                  { value: 'GBP', label: 'GBP - British Pound' },
                ]}
                value={watch('currency')}
                onValueChange={(value) => setValue('currency', value)}
                disabled={(isEditing && !unlocked) || systemLocked}
                placeholder={t('accountModal.selectCurrency')}
                searchPlaceholder="Search..."
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t('accountModal.description')}</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder={t('accountModal.descriptionPlaceholder')}
                rows={2}
              />
            </div>

            {/* System Account Warning */}
            {account?.isSystemAccount === true && (
              <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-700">
                {unlocked
                  ? t('accountModal.systemAccountPrepWarning')
                  : t('accountModal.systemAccountWarning')}
              </div>
            )}

            {/* What the change moved — reported by the server, never blocking */}
            {warnings.length > 0 && (
              <div className="space-y-1 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                <p className="font-medium">{t('accountModal.impactTitle')}</p>
                <ul className="list-disc space-y-1 pl-4">
                  {warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('accountModal.cancel')}
            </Button>
            {warnings.length > 0 ? (
              <Button type="button" onClick={() => onOpenChange(false)}>
                {t('accountModal.acknowledge')}
              </Button>
            ) : (
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? t('accountModal.saveChanges') : t('accountModal.createAccount')}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
